import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import jsQR from "jsqr";
import { X, Camera, AlertTriangle, Image as ImageIcon, Loader2 } from "lucide-react";

/**
 * QRScanModal — opens device camera, decodes any QR, then calls onResult(text).
 *
 * The parent passes a `parse` function that takes the raw decoded text and
 * returns either a string to fill (e.g. "RAHIM2026") or null to keep scanning.
 * If `parse` is not supplied, the raw text is returned.
 *
 * Falls back to image-file upload (gallery) when camera isn't available
 * (desktop without webcam, denied permission, iOS in-app browser, etc.).
 */
export default function QRScanModal({ open, onClose, onResult, parse }) {
  const elRef = useRef(null);
  const scannerRef = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle | starting | scanning | error | success
  const [errMsg, setErrMsg] = useState("");
  const [decoded, setDecoded] = useState("");
  const fileInputRef = useRef(null);

  // Start camera when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const start = async () => {
      setPhase("starting");
      setErrMsg("");
      setDecoded("");
      try {
        if (!elRef.current) return;
        const scanner = new Html5Qrcode(elRef.current.id, { verbose: false });
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: "environment" }, // prefer rear camera on mobile
          {
            fps: 10,
            qrbox: (vw, vh) => {
              const m = Math.min(vw, vh);
              const box = Math.round(m * 0.72);
              return { width: box, height: box };
            },
            aspectRatio: 1.0,
          },
          (text) => {
            if (cancelled) return;
            const parsed = parse ? parse(text) : text;
            if (!parsed) return; // keep scanning if parser rejects
            setDecoded(parsed);
            setPhase("success");
            // Stop scanner then bubble up the result
            scanner.stop().catch(() => {}).finally(() => {
              onResult?.(parsed);
            });
          },
          // ignore per-frame decode errors silently
          () => {},
        );
        if (!cancelled) setPhase("scanning");
      } catch (e) {
        if (cancelled) return;
        const msg = (e && e.message) || String(e || "");
        // Friendly message mapping
        if (/NotAllowed|Permission/i.test(msg))
          setErrMsg("Camera permission denied. Allow camera access or upload an image instead.");
        else if (/NotFound|no camera|Requested device/i.test(msg))
          setErrMsg("No camera found. Upload a QR image instead.");
        else if (/NotReadable|in use/i.test(msg))
          setErrMsg("Camera is busy. Close other apps using the camera and try again.");
        else
          setErrMsg(msg || "Could not start camera");
        setPhase("error");
      }
    };

    start();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        // Just release the camera stream. We deliberately DO NOT call
        // `clear()` here because it tries to removeChild() the video element,
        // which races with React's own unmount and throws `NotFoundError:
        // The node to be removed is not a child of this node`. Letting React
        // tear down the container div is safe — the html5-qrcode worker is
        // garbage-collected together with its node.
        const isRunning = typeof s.isScanning === "boolean" ? s.isScanning : true;
        if (isRunning) {
          s.stop().catch(() => {});
        }
      }
    };
  }, [open, onResult, parse]);

  // Image-file fallback. Uses jsQR (pure JS, no camera dependency) so it
  // never conflicts with the live html5-qrcode camera scanner. We also
  // proactively stop the live camera so resources are released while the
  // file is decoded — important on low-end Android devices.
  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhase("starting");
    setErrMsg("");

    // Stop the live camera if it was running so we don't fight for resources
    if (scannerRef.current) {
      const s = scannerRef.current;
      scannerRef.current = null;
      try {
        const isRunning = typeof s.isScanning === "boolean" ? s.isScanning : true;
        if (isRunning) await s.stop();
      } catch { /* noop */ }
      // NOTE: deliberately not calling s.clear() — that triggers DOM removeChild
      // which races with React's unmount. The container div is owned by React.
    }

    try {
      // Load file into an Image
      const img = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const im = new Image();
          im.onload = () => resolve(im);
          im.onerror = () => reject(new Error("Image could not be loaded"));
          im.src = reader.result;
        };
        reader.onerror = () => reject(new Error("File read failed"));
        reader.readAsDataURL(file);
      });

      // Try jsQR at multiple downscaled sizes (helps with very large photos +
      // makes decoding more tolerant of camera-roll JPGs that have lots of
      // surrounding context).
      const tryDecode = (maxSide) => {
        const ratio = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
        const cw = Math.max(1, Math.round(img.naturalWidth * ratio));
        const ch = Math.max(1, Math.round(img.naturalHeight * ratio));
        const cnv = document.createElement("canvas");
        cnv.width = cw; cnv.height = ch;
        const ctx = cnv.getContext("2d", { willReadFrequently: true });
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, cw, ch);
        ctx.drawImage(img, 0, 0, cw, ch);
        const data = ctx.getImageData(0, 0, cw, ch);
        const r1 = jsQR(data.data, cw, ch, { inversionAttempts: "attemptBoth" });
        return r1?.data || null;
      };

      let text = tryDecode(1200);
      if (!text) text = tryDecode(800);
      if (!text) text = tryDecode(400);
      if (!text) text = tryDecode(2000);

      if (!text) {
        setErrMsg("No QR code detected in this image. Try a clearer photo.");
        setPhase("error");
        return;
      }

      const parsed = parse ? parse(text) : text;
      if (!parsed) {
        setErrMsg("QR detected but no valid referral / promo code inside.");
        setPhase("error");
        return;
      }
      setDecoded(parsed);
      setPhase("success");
      onResult?.(parsed);
    } catch (err) {
      setErrMsg(err?.message || "Could not read QR from image");
      setPhase("error");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="qr-scan-modal"
    >
      <div
        className="w-full sm:max-w-[420px] rounded-t-3xl sm:rounded-2xl overflow-hidden"
        style={{
          background: "#0c1420",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 -24px 60px -20px rgba(0,0,0,0.6)",
        }}
      >
        {/* drag handle */}
        <div className="flex justify-center pt-2 sm:hidden">
          <div className="h-1 w-10 rounded-full bg-white/15" />
        </div>

        {/* header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="h-8 w-8 rounded-lg flex items-center justify-center bg-emerald-500/15 border border-emerald-400/30 shrink-0">
              <Camera size={15} className="text-emerald-300" />
            </span>
            <div className="min-w-0">
              <h3 className="text-white font-bold text-[16px] leading-tight">Scan referral / promo QR</h3>
              <p className="text-white/45 text-[11px] mt-0.5 truncate">
                Point at a Maxlive88 invite or promo QR code
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full inline-flex items-center justify-center text-white/60 hover:text-white hover:bg-white/[0.06] transition shrink-0"
            data-testid="qr-scan-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* camera viewport — outer wrapper is React-owned; inner #qr-scan-viewport
            is fully owned by html5-qrcode (no React children) so React's
            unmount never races with the library's DOM mutations. Overlays sit
            in a sibling positioned-absolute div that React owns exclusively. */}
        <div className="px-4">
          <div
            className="relative w-full aspect-square rounded-xl overflow-hidden"
            style={{ background: "#000" }}
            data-testid="qr-scan-viewport"
          >
            {/* html5-qrcode's exclusive territory — no JSX children inside */}
            <div
              id="qr-scan-viewport"
              ref={elRef}
              className="absolute inset-0"
            />

            {/* React-owned overlay layer — sibling of the library's div */}
            <div className="absolute inset-0 pointer-events-none z-10">
              {phase === "starting" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white/60 gap-2">
                  <Loader2 size={28} className="animate-spin" />
                  <div className="text-[12px]">Starting camera…</div>
                </div>
              )}
              {phase === "error" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 gap-2 bg-black/40">
                  <AlertTriangle size={26} className="text-amber-300" />
                  <div className="text-amber-100 text-[12.5px] font-semibold">{errMsg}</div>
                </div>
              )}
              {phase === "success" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 gap-2 bg-emerald-500/15 backdrop-blur-sm">
                  <div className="text-emerald-200 text-[12px] uppercase tracking-[2px] font-bold">Detected</div>
                  <div className="text-white font-black text-[20px] tracking-[0.18em] break-all" data-testid="qr-scan-decoded">
                    {decoded}
                  </div>
                </div>
              )}
              {phase === "scanning" && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-[72%] aspect-square rounded-2xl">
                    {["tl","tr","bl","br"].map((c) => (
                      <span
                        key={c}
                        className="absolute h-7 w-7 border-emerald-300"
                        style={{
                          borderTopWidth: c.includes("t") ? 3 : 0,
                          borderBottomWidth: c.includes("b") ? 3 : 0,
                          borderLeftWidth: c.includes("l") ? 3 : 0,
                          borderRightWidth: c.includes("r") ? 3 : 0,
                          top: c.includes("t") ? -1 : "auto",
                          bottom: c.includes("b") ? -1 : "auto",
                          left: c.includes("l") ? -1 : "auto",
                          right: c.includes("r") ? -1 : "auto",
                          borderRadius: 4,
                        }}
                      />
                    ))}
                    <div
                      className="absolute left-2 right-2 h-[2px] bg-emerald-400"
                      style={{
                        top: "50%",
                        boxShadow: "0 0 12px rgba(16,185,129,0.8)",
                        animation: "qr-scan-line 2s ease-in-out infinite",
                      }}
                    />
                    <style>{`
                      @keyframes qr-scan-line {
                        0%   { top: 8%; opacity: 0.4; }
                        50%  { top: 92%; opacity: 1; }
                        100% { top: 8%; opacity: 0.4; }
                      }
                    `}</style>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-4 pt-3 pb-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="h-11 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-2 transition active:scale-95"
            data-testid="qr-scan-upload"
          >
            <ImageIcon size={14} /> Upload image
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-200 text-[12.5px] font-bold inline-flex items-center justify-center gap-2 transition active:scale-95"
            data-testid="qr-scan-cancel"
          >
            Cancel
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPickFile}
            data-testid="qr-scan-file-input"
          />
        </div>

        <div className="px-5 pb-4 text-[10.5px] text-white/35 leading-snug">
          Allow camera access when prompted. We never store the video — scanning happens entirely in your browser.
        </div>

        {/* iOS safe area */}
        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
}
